import {Pagination} from 'api/pagination';
import {AnimalParents} from 'api/query/animal/animal-parents';
import catalogueApi, {SaleType} from 'api/query/catalogue';
import {SearchAnimal} from 'api/query/search/search-animal';
import SearchApi from 'api/query/search/search-api';
import {SearchParamsIds} from 'api/query/search/search-params-ids';
import {SearchResponse} from 'api/query/search/search-response';
import {SearchSort} from 'api/query/search/search-sort';
import Axios, {CancelTokenSource} from 'axios';
import BreedingServiceLogo from 'components/breeding-service-logo';
import {DropdownItem} from 'components/dropdown/dropdown-item';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import moment from 'moment';
import {getAnimalIdsFromCatalogue} from 'pages/catalogues/catalogue-utils';
import {SortDir} from 'pages/search/results/sort-dir.enum';
import QRCode from 'qrcode.react';
import React, {useCallback, useEffect, useState} from 'react';
import {AnimalId} from 'shared/animal-id.constant';
import {toQuerystring} from '../../../../utils/navigation-utils';
import {LogosEnum} from '../../../report/export-pdf/pdf-logo/logos-enum';
import InfoSheet from '../info-sheet';
import {PenCardsContainerProps} from './pen-cards-container-props';

/**
 * Pen cards container.
 * @param props
 */
export const PenCardsContainer: React.FC<PenCardsContainerProps> = (props) => {
  const {
    catalogue,
    penCardImg,
    db,
    cardLoaded,
    cardFailed,
    user, traits, asbvs,
  } = props;
  const [data, setData] = useState<SearchResponse>({
    results: [],
    analysisDate: '',
    sortBy: '',
    sortDir: SortDir.BestToWorst,
    totalCount: 0,
  });
  const [parents, setParents] = useState<AnimalParents[]>();

  /**
   * Calls search api to get animals details that are listed in catalogue.
   */
  const loadResults = useCallback(async (page: Pagination, sortOption: SearchSort, source: CancelTokenSource) => {
    let animalsIds: string;
    if (catalogue?.catalogueItems) {
      animalsIds = getAnimalIdsFromCatalogue(catalogue.catalogueItems);
    } else {
      animalsIds = '';
    }

    // gets ids of selected asbvs
    const selectedAsbvs = asbvs?.filter((el: DropdownItem) => el.id).map((el: DropdownItem) => el.id).toString() || '';

    const params: SearchParamsIds = {
      db,
      userType: user,
      subIndex: traits || undefined,
      customASBVs: selectedAsbvs,
    };

    return SearchApi.getByIds(catalogue.id, params, page, sortOption, source.token);
  }, [asbvs, catalogue, db, traits, user]);

  /**
   * Calls animal api to get parents for animals from catalogue.
   */
  const loadParents = useCallback(async (source: CancelTokenSource) => catalogueApi.getAnimalParentsByCatalogue(catalogue.id, db, source.token), [catalogue, db]);

  const compareAlphaNumStrings = (n1: string | null, n2: string | null) => {
    if (!n1 || !n2) return 0;
    // extract the leading numerical values
    const num1 = parseInt(n1);
    const num2 = parseInt(n2);
    if (!isNaN(num1) && !isNaN(num2) && num1 !== num2) {
      return num1 - num2;
    }

    // if num1, num2 cannot be sorted numerically (or are equal), sort as strings
    return n1.localeCompare(n2);
  };  

  const generatePdf = () => {
    window.scrollTo(0, 0); // Attempt to fix issue where HTML elements are being cut off
    setTimeout(() => {
      // version for multiple cards by targeting every card
      const doc = new jsPDF({
        orientation: 'l',
        unit: 'mm',
        format: 'a4',
        compress: true,
      });
      const position = 0;

      // eslint-disable-next-line no-unused-expressions
      const createPDFpages = async () => {
        if (catalogue) {
          const imgWidth = doc.internal.pageSize.getWidth();
          const infoSheet = document.getElementById('info-sheet');
          const logoImg = document.getElementById('logo-img');
          const analysisLogo = document.getElementById('analysis-logo');
          let analysisLogoData = '';
          let logoSize = { w: 60, h: 60 };

          await html2canvas(infoSheet as HTMLDivElement, { scrollY: -window.scrollY })
            .then((canvas) => {
              const imgData = canvas.toDataURL('image/png', 1.0);
              const imgHeight = (canvas.height * imgWidth) / canvas.width;

              doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight, undefined, 'FAST');
            });

          await html2canvas(logoImg as HTMLDivElement, { scrollY: -window.scrollY })
            .then((canvas) => {
              logoSize = { w: canvas.width, h: canvas.height };
            });

          await html2canvas(analysisLogo as HTMLDivElement, { scrollY: -window.scrollY })
            .then((canvas) => {
              analysisLogoData = canvas.toDataURL('image/png', 1.0);
            });
          
          catalogue.catalogueItems.sort((item1, item2) =>
            compareAlphaNumStrings(item1.lotNo, item2.lotNo)
          );
          
          for (const item of catalogue.catalogueItems) {
            doc.addPage();
            // Set color to grey
            doc.setTextColor('#4B4F54');
            // Add qr-code
            const qrCode = document.getElementById(`qr-code-${item.animalId}`);
            // eslint-disable-next-line no-await-in-loop
            await html2canvas(qrCode as HTMLDivElement, { scrollY: -window.scrollY })
              .then((canvas) => {
                const imgData = canvas.toDataURL('image/png', 1.0);
                doc.addImage(imgData, 'PNG', 10, 10, 60, 60, `qr-code-${item.animalId}`, 'FAST');
              });
            // Add logo
            if (penCardImg) {
              doc.addImage(penCardImg, 'PNG', 220, 10, 60, ((logoSize.h * 60) / logoSize.w), 'logo', 'FAST');
            }
            // animal info header
            const animal = data.results.find((i) => i.animalId === item.animalId);
            doc.setFontSize(18);
            doc.setFont(undefined, 'bold');
            doc.text(`${item.lotNo ? 'LOT' : ''} ${item.lotNo || ''}`, 90, 20);
            doc.setFontSize(30);
            doc.text(animal?.studId.replace('-', '-\n'), 90, 40);
            doc.setFontSize(18);
            doc.text(item.displayAnimalId, 90, 70);

            // animal parents
            doc.setFontSize(14);
            doc.setFont(undefined, 'normal');
            doc.text(`SIRE\t${parents?.find(((i) => i.animalId === item.animalId))?.sireStudId}`, 10, 85);
            doc.text(`DAM \t${parents?.find(((i) => i.animalId === item.animalId))?.damStudId}`, 10, 90);
            // asbvs
            let xPosition = 15;
            // eslint-disable-next-line no-unused-expressions
            animal?.asbvs.forEach((asbv) => {
              doc.setFontSize(16);
              doc.setFont(undefined, 'bold');
              doc.text(asbv.name, xPosition, 120);
              doc.setFontSize(20);
              doc.setTextColor('black');
              doc.text(asbv.value, xPosition, 135);
              doc.setFontSize(12);
              doc.setTextColor('#4B4F54');
              doc.setFont(undefined, 'normal');
              doc.text(`ACC. ${asbv.accuracy}`, xPosition, 145);
              // percentile badge
              doc.setFont(undefined, 'bold');
              if (!asbv.isRbv && asbv.hasLeader) {
                if (asbv.percentile === 0) {
                  doc.setTextColor('#6d3075');
                  doc.setDrawColor('#f0e5ef');
                  doc.setFillColor('#f0e5ef');
                  doc.roundedRect(xPosition - 5, 149, 25, 10, 5, 5, 'FD');
                  doc.text('TOP 5%', xPosition, 155);
                } else if (asbv.percentile === 10) {
                  doc.setTextColor('#00768e');
                  doc.setDrawColor('#dbf3f8');
                  doc.setFillColor('#dbf3f8');
                  doc.roundedRect(xPosition - 5, 149, 27, 10, 5, 5, 'FD');
                  doc.text('TOP 10%', xPosition, 155);
                } else if (asbv.percentile === 20) {
                  doc.setTextColor('#487c06');
                  doc.setDrawColor('#f2f8e9');
                  doc.setFillColor('#f2f8e9');
                  doc.roundedRect(xPosition - 5, 149, 27, 10, 5, 5, 'FD');
                  doc.text('TOP 20%', xPosition, 155);
                }
              }
              xPosition += 35;
              doc.setTextColor('#4B4F54');
            });
            // Footer
            doc.addImage(analysisLogoData, 'PNG', 10, 185, 20, 20, 'ASBV-logo', 'FAST');
            doc.setFontSize(12);
            doc.setFont(undefined, 'bold');
            doc.text(`Analysis Date: ${data.analysisDate}`, 35, 190);
            doc.setFontSize(10);
            doc.setFont(undefined, 'normal');
            doc.text('Sheep Genetics ASBVs are calculated from raw data collected by breeders and accredited Sheep Genetics operators.\nSheep Genetics (a program of Meat & Livestock Australia) does not oversee or audit the collection of this data therefore\nSheep Genetics does not accept any responsibility for the accuracy of the information in this document.', 35, 195);
            doc.addImage(LogosEnum.sg, 'JPEG', 240, 193, 47.3, 7.2, 'SheepGenetics-logo', 'FAST');
          }
        }

        // waits for generating all pages and then saves pdf
        doc.save(`Pen Card_${catalogue?.title}_${moment().format('DD MMM YYYY')}.pdf`);
      };

      createPDFpages().then(() => {
        cardLoaded(true);
      });
    }, 2000);
  };
  /*
   * Initial results retrieve.
   */
  useEffect(() => {
    const source = Axios.CancelToken.source();

    const sortOption = {
      sortBy: catalogue.saleType === SaleType.AllSemenCatalogue ? AnimalId : 'lot',
      sortDir: SortDir.BestToWorst,
    };

    const paging = {
      pageNumber: 1,
      pageSize: catalogue.catalogueItems.length, // amount of all animals
    };

    const getResults = async () => {
      try {
        const animalsData = await loadResults(paging, sortOption, source);
        const parentsData = await loadParents(source);
        setData(animalsData);
        setParents(parentsData);
      } catch (e) {
        cardFailed();
      }
    };
    getResults();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogue.catalogueItems.length, catalogue.saleType, loadResults]);

  /**
   * Triggers generating pdf in `create pen card`.
   */
  useEffect(() => {
    if (data.results.length && parents) {
      generatePdf();
    }
  }, [data.results.length, generatePdf, parents]);

  return (
    <div
      className="sg-create-pen-card__cards-container"
      id="pen-cards-container"
    >
      <InfoSheet db={db} />
      <img
        id="logo-img"
        src={penCardImg}
        alt="Logo"
      />
      <div
        id="analysis-logo"
        style={{ width: '200px', height: '200px' }}
      >
        <BreedingServiceLogo db={db} />
      </div>
      {
        parents && data.results.map((animal: SearchAnimal) => (
          <QRCode
            id={`qr-code-${animal.animalId}`}
            size={185}
            value={`${window.location.origin}/animal/${animal.animalId}${toQuerystring({ db, scenario: traits })}`}
          />
        ))
      }
    </div>
  );
};

(PenCardsContainer as any).whyDidYouRender = true;
